import React from 'react'
import PropTypes from 'prop-types'
import { Form } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import { useSelector } from 'react-redux'
import { diff } from 'deep-object-diff'
import isEmpty from 'lodash/isEmpty'
import {
  setPropType,
  impressionsPropType,
  setFullPropType,
  retailerPropType,
} from 'const/propTypes'
import {
  deletedSets,
  getFlagReinitialize,
  getIsLoaded,
  getIsSavingActive,
} from 'domains/retailers/selectors'
import { Buttons, StyledButton } from './ImagesForm.styles'
import Sets from './Sets'
import { FIELD_NAMES } from 'const/forms'
import FormStateToRedux from 'components/FormStateToRedux'
import { Tooltip } from 'antd'
import {
  hasAllGenders,
  getErrorText,
  getErrorTextForSync,
} from 'helpers/images'

const ImagesForm = ({
  formName,
  closeModal,
  onSubmit,
  initialValues,
  fullData,
  retailer,
  impressions,
}) => {
  const isSavingActive = useSelector(getIsSavingActive)
  const isLoaded = useSelector(getIsLoaded)
  const deletedFields = useSelector(deletedSets)
  const isReinitialize = useSelector(getFlagReinitialize)

  return (
    <Form
      destroyOnUnregister
      keepDirtyOnReinitialize={isReinitialize}
      initialValues={initialValues}
      onSubmit={onSubmit}
      mutators={{
        ...arrayMutators,
      }}
      render={({
        handleSubmit,
        hasValidationErrors,
        values,
        form: { change },
      }) => {
        const filteredValues = {
          ...values,
          [FIELD_NAMES.set]:
            values[FIELD_NAMES.set]?.map((set, index) =>
              deletedFields.includes(index) ? null : set
            ) || [],
        }
        const hasSet = !!(
          values[FIELD_NAMES.set]?.length - deletedFields.length
        )
        const isEmptyChanges = isEmpty(diff(initialValues, filteredValues))
        const hasLockedSets = initialValues[FIELD_NAMES.set].some(
          (set) => set?.[FIELD_NAMES.isLocked]
        )
        const hasRegional = !isEmpty(retailer[FIELD_NAMES.unavailableRegions])
        const disableSaveButton =
          hasValidationErrors || isSavingActive || isEmptyChanges || !isLoaded
        const disableCopying =
          hasValidationErrors ||
          !isEmptyChanges ||
          !isLoaded ||
          isSavingActive ||
          !hasSet ||
          hasLockedSets
        const disableRefreshButton = !isEmptyChanges || isSavingActive
        return (
          <>
            <form>
              <FormStateToRedux form={formName} onChangeFieldState={change} />
              <Sets
                disableCopyingReason={getErrorTextForSync({
                  hasValidationErrors,
                  hasChanges: !isEmptyChanges,
                  hasSet,
                  hasLockedSets,
                  hasRegional,
                })}
                disableCopying={disableCopying}
                retailer={retailer}
                showInfo={
                  !hasSet && isLoaded && !initialValues[FIELD_NAMES.set]?.length
                }
                impressions={impressions}
                values={values}
                fullData={fullData?.[FIELD_NAMES.set]}
                disableRefreshButton={disableRefreshButton}
              />
              <Buttons>
                <StyledButton onClick={closeModal}>Cancel</StyledButton>
                <Tooltip
                  title={getErrorText({
                    hasAllGenders: hasAllGenders(filteredValues),
                    hasSet,
                    hasValidationErrors,
                    isEmptyChanges,
                  })}
                >
                  <StyledButton
                    type="primary"
                    onClick={handleSubmit}
                    disabled={disableSaveButton}
                  >
                    Save changes
                  </StyledButton>
                </Tooltip>
              </Buttons>
            </form>
          </>
        )
      }}
    />
  )
}

ImagesForm.propTypes = {
  formName: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    [FIELD_NAMES.logo]: PropTypes.string,
    [FIELD_NAMES.set]: PropTypes.arrayOf(setPropType),
  }),
  fullData: PropTypes.shape({
    [FIELD_NAMES.logo]: PropTypes.string,
    [FIELD_NAMES.set]: PropTypes.arrayOf(setFullPropType),
  }),
  impressions: impressionsPropType.isRequired,
  retailer: retailerPropType.isRequired,
}

export default ImagesForm
